import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/api';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Shield, AlertTriangle, CheckCircle, Book, BarChart3,
  FileCode, GitBranch, Container, Server, Folder,
  RefreshCw, ChevronDown, ChevronRight, ExternalLink,
  AlertCircle, Loader2, Network, Share2, Filter,
  Layers, Database, Cloud, Settings, Zap
} from 'lucide-react';

function ScoreGauge({ score, size = 'large' }) {
  const getColor = (s) => {
    if (s >= 80) return { ring: 'stroke-success', bg: 'bg-success/10', text: 'text-success' };
    if (s >= 60) return { ring: 'stroke-warning', bg: 'bg-warning/10', text: 'text-warning' };
    return { ring: 'stroke-destructive', bg: 'bg-destructive/10', text: 'text-destructive' };
  };

  const getGrade = (s) => {
    if (s >= 90) return 'A';
    if (s >= 80) return 'B';
    if (s >= 70) return 'C';
    if (s >= 60) return 'D';
    return 'F';
  };

  const colors = getColor(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  if (size === 'small') {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg}`}>
        <span className={`text-sm font-bold ${colors.text}`}>{score.toFixed(0)}</span>
        <span className={`text-xs ${colors.text}`}>/ 100</span>
      </div>
    );
  }

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg className="w-32 h-32 transform -rotate-90">
        <circle
          cx="64"
          cy="64"
          r="45"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-gray-200"
        />
        <circle
          cx="64"
          cy="64"
          r="45"
          strokeWidth="8"
          fill="none"
          className={colors.ring}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`text-3xl font-bold ${colors.text}`}>{getGrade(score)}</span>
        <span className="text-sm text-gray-500">{score.toFixed(0)}%</span>
      </div>
    </div>
  );
}

function BreakdownBar({ label, value, weight, icon: Icon }) {
  const getBarColor = (v) => {
    if (v >= 80) return 'bg-success';
    if (v >= 60) return 'bg-warning';
    return 'bg-destructive';
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-400" />}
          <span className="text-gray-700">{label}</span>
          <span className="text-xs text-gray-400">({weight}%)</span>
        </div>
        <span className="font-medium text-gray-900">{value.toFixed(1)}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${getBarColor(value)} transition-all duration-500`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

function ArtifactCard({ title, count, icon: Icon, items, color = 'blue' }) {
  const [expanded, setExpanded] = useState(false);

  const colorClasses = {
    blue: 'bg-info/10 border-info/30 text-info',
    green: 'bg-success/10 border-success/30 text-success',
    purple: 'bg-primary/10 border-primary/30 text-primary',
    orange: 'bg-warning/10 border-warning/30 text-warning',
    red: 'bg-destructive/10 border-destructive/30 text-destructive',
    teal: 'bg-info/10 border-info/30 text-info',
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => items && items.length > 0 && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <div>
            <div className="font-medium text-gray-900">{title}</div>
            <div className="text-sm text-gray-500">{count} found</div>
          </div>
        </div>
        {items && items.length > 0 && (
          expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </div>
      {expanded && items && items.length > 0 && (
        <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
          {items.slice(0, 10).map((item, idx) => (
            <div key={idx} className="text-sm bg-white rounded px-3 py-2 border border-gray-100">
              <div className="font-medium text-gray-800">{item.name || item.title || item.kind || 'Unnamed'}</div>
              <div className="text-xs text-gray-500">{item.file_path}</div>
            </div>
          ))}
          {items.length > 10 && (
            <div className="text-xs text-gray-500 text-center py-1">
              + {items.length - 10} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IssueItem({ issue }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
      <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
      <div className="text-sm text-destructive">{issue}</div>
    </div>
  );
}

function RecommendationItem({ recommendation }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-info/10 border border-info/30 rounded-lg">
      <CheckCircle className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
      <div className="text-sm text-info">{recommendation}</div>
    </div>
  );
}

// Knowledge Graph Visualization Component
function KnowledgeGraphVisualization({ repoPath, onNodeClick }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    services: true,
    monitoring: true,
    infrastructure: true,
    cicd: true,
    documentation: true,
  });

  const loadKnowledgeGraph = useCallback(async () => {
    if (!repoPath) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        include_services: filters.services,
        include_monitoring: filters.monitoring,
        include_infrastructure: filters.infrastructure,
        include_cicd: filters.cicd,
        include_documentation: filters.documentation,
      });

      const response = await api.get(
        `/oncall/knowledge-graph/visualize/${encodeURIComponent(repoPath)}?${params}`
      );

      setGraphData(response.data);
      setNodes(response.data.nodes || []);
      setEdges(response.data.edges || []);
    } catch (err) {
      console.error('Failed to load knowledge graph:', err);
      if (err.response?.status === 404) {
        setError('Knowledge graph not built yet. Use "Build Graph" to create it.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load knowledge graph');
      }
    } finally {
      setLoading(false);
    }
  }, [repoPath, filters, setNodes, setEdges]);

  const buildKnowledgeGraph = async () => {
    if (!repoPath) return;

    setLoading(true);
    setError(null);

    try {
      await api.post('/oncall/knowledge-graph/build', {
        repo_path: repoPath,
        compute_relationships: true,
      });

      // Reload the graph after building
      await loadKnowledgeGraph();
    } catch (err) {
      console.error('Failed to build knowledge graph:', err);
      setError(err.response?.data?.detail || 'Failed to build knowledge graph');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKnowledgeGraph();
  }, [loadKnowledgeGraph]);

  const handleNodeClick = useCallback((event, node) => {
    if (onNodeClick) {
      onNodeClick(node.data);
    }
  }, [onNodeClick]);

  const toggleFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filterConfig = [
    { key: 'services', label: 'Services', icon: Server, color: 'hsl(var(--info))' },
    { key: 'monitoring', label: 'Monitoring', icon: AlertTriangle, color: 'hsl(var(--destructive))' },
    { key: 'infrastructure', label: 'Infrastructure', icon: Cloud, color: 'hsl(var(--success))' },
    { key: 'cicd', label: 'CI/CD', icon: GitBranch, color: 'hsl(var(--warning))' },
    { key: 'documentation', label: 'Runbooks', icon: Book, color: 'hsl(var(--info))' },
  ];

  const getMinimapNodeColor = (node) => {
    return node.data?.color || 'hsl(var(--muted-foreground))';
  };

  if (loading && nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600">Loading knowledge graph...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters:</span>
          <div className="flex items-center gap-2 ml-2">
            {filterConfig.map(({ key, label, icon: Icon, color }) => (
              <button
                key={key}
                onClick={() => toggleFilter(key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${filters[key]
                  ? 'bg-white border-2 shadow-sm'
                  : 'bg-gray-200 text-gray-500'
                  }`}
                style={{
                  borderColor: filters[key] ? color : 'transparent',
                  color: filters[key] ? color : undefined,
                }}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadKnowledgeGraph}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={buildKnowledgeGraph}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm transition-colors"
          >
            <Layers className="w-4 h-4" />
            Build Graph
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
          <p className="mt-2 text-sm text-yellow-600">
            Click "Build Graph" to create the knowledge graph for this repository.
          </p>
        </div>
      )}

      {/* Legend */}
      {graphData?.legend && graphData.legend.length > 0 && (
        <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-3">
          <span className="text-sm font-medium text-gray-500">Legend:</span>
          {graphData.legend.map(({ category, color, backgroundColor, count }) => (
            <div key={category} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm border-2"
                style={{ backgroundColor, borderColor: color }}
              />
              <span className="text-xs text-gray-600 capitalize">
                {category} ({count})
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Graph */}
      <div className="h-[500px] bg-white border border-gray-200 rounded-lg overflow-hidden">
        {nodes.length > 0 ? (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            maxZoom={2}
          >
            <Controls />
            <MiniMap nodeColor={getMinimapNodeColor} />
            <Background variant="dots" gap={16} size={1} />
          </ReactFlow>
        ) : !loading && !error ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Share2 className="w-12 h-12 mb-4 text-gray-300" />
            <p className="font-medium">No knowledge graph available</p>
            <p className="text-sm mt-1">Click "Build Graph" to create one</p>
          </div>
        ) : null}
      </div>

      {/* Statistics */}
      {graphData?.statistics && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {graphData.statistics.total_nodes}
            </div>
            <div className="text-xs text-gray-500">Total Nodes</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {graphData.statistics.total_edges}
            </div>
            <div className="text-xs text-gray-500">Total Edges</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {Object.keys(graphData.statistics.categories || {}).length}
            </div>
            <div className="text-xs text-gray-500">Categories</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {graphData.statistics.categories?.service || 0}
            </div>
            <div className="text-xs text-gray-500">Services</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OnCallReadinessPanel({ onNavigateToCodeGraph }) {
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [repoPath, setRepoPath] = useState('');
  const [scannedRepos, setScannedRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedGraphNode, setSelectedGraphNode] = useState(null);

  // Data Readiness State
  const [readinessReport, setReadinessReport] = useState(null);
  const [scanningReadiness, setScanningReadiness] = useState(false);

  // GitHub connected repos
  const [githubRepos, setGithubRepos] = useState([]);
  const [loadingGithub, setLoadingGithub] = useState(false);
  const [githubConnected, setGithubConnected] = useState(false);

  // Load scanned repositories and GitHub repos on mount
  useEffect(() => {
    loadScannedRepos();
    loadGithubRepos();
  }, []);

  // Load GitHub connected repos
  const loadGithubRepos = async () => {
    setLoadingGithub(true);
    try {
      const response = await api.get('/github/status');
      if (response.data.connected) {
        setGithubConnected(true);
        // repos_selected is an array of strings like "owner/repo"
        const repoNames = response.data.repos_selected || [];
        // Convert to objects with full_name property
        setGithubRepos(repoNames.map(name => ({ full_name: name })));
      }
    } catch (err) {
      console.error('Failed to load GitHub repos:', err);
    } finally {
      setLoadingGithub(false);
    }
  };

  // Scan a GitHub repo
  const scanGithubRepo = async (repoFullName) => {
    const repoUrl = `https://github.com/${repoFullName}`;
    setRepoPath(repoUrl);
    setLoading(true);
    setError(null);
    setScanResult(null);
    try {
      const response = await api.post('/oncall/scan', { repo_path: repoUrl });
      setScanResult(response.data);
      await loadScannedRepos();
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.response?.data?.detail || 'Failed to scan repository');
    } finally {
      setLoading(false);
    }
  };

  const loadScannedRepos = async () => {
    setLoadingRepos(true);
    try {
      const response = await api.get('/oncall/repositories');
      const repos = response.data.repositories || [];

      // Fetch code indexing status for each repo
      const reposWithCodeStatus = await Promise.all(
        repos.map(async (repo) => {
          try {
            // Extract repo_key from repo_path (GitHub URL)
            let repoKey = repo.repo_key;
            if (!repoKey && repo.repo_path) {
              const match = repo.repo_path.match(/github\.com\/([^\/]+\/[^\/]+)/);
              if (match) {
                repoKey = match[1].replace('.git', '');
              }
            }

            // Check if code is indexed
            if (repoKey) {
              try {
                const codeResponse = await api.get(`/code-graph/repos`);
                const indexedRepos = codeResponse.data.repositories || [];
                const isIndexed = indexedRepos.some(r => r.repo_key === repoKey);
                return { ...repo, code_indexed: isIndexed, repo_key: repoKey };
              } catch (err) {
                // If code graph endpoint fails, assume not indexed
                return { ...repo, code_indexed: false, repo_key: repoKey };
              }
            }
            return { ...repo, code_indexed: false };
          } catch (err) {
            return { ...repo, code_indexed: false };
          }
        })
      );

      setScannedRepos(reposWithCodeStatus);
    } catch (err) {
      console.error('Failed to load scanned repositories:', err);
    } finally {
      setLoadingRepos(false);
    }
  };

  const runScan = async () => {
    if (!repoPath.trim()) {
      setError('Please enter a repository path');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/oncall/scan', { repo_path: repoPath.trim() });
      setScanResult(response.data);
      // Reload scanned repos after successful scan
      await loadScannedRepos();
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.response?.data?.detail || 'Failed to scan repository');
    } finally {
      setLoading(false);
    }
  };

  const handleScanReadiness = async () => {
    setScanningReadiness(true);
    try {
      const res = await api.post('/oncall/data-readiness/report', { scan_all: true });
      setReadinessReport(res.data);
    } catch (err) {
      console.error("Readiness scan failed", err);
    } finally {
      setScanningReadiness(false);
    }
  };

  const selectRepo = async (repo) => {
    setRepoPath(repo.repo_path);
    setError(null);
    setLoading(true);
    setScanResult(null); // Clear previous result

    // Automatically load the stored scan result
    if (repo.latest_scan) {
      try {
        // Try to get stored scan result
        const response = await api.get(`/oncall/repositories/${encodeURIComponent(repo.repo_path)}/scan-result`);
        setScanResult(response.data);
        setActiveTab('overview'); // Switch to overview tab to show results
      } catch (err) {
        console.error('Failed to load stored scan result:', err);
        // If stored result not available, offer to run a new scan
        if (err.response?.status === 404) {
          setError('Stored scan result not found. Click "Scan" to run a new scan.');
        } else {
          setError('Failed to load scan results. Please try scanning again.');
        }
      } finally {
        setLoading(false);
      }
    } else {
      // No scan available, just set the path
      setLoading(false);
      setError('No scan data available for this repository. Click "Scan" to analyze it.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      runScan();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center shadow-sm">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">OnCall Readiness Scanner</h2>
            <p className="text-sm text-gray-500">Analyze operational readiness of your infrastructure</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column: Global Check */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              Check All Data
              {scanningReadiness && <Loader2 className="w-3 h-3 animate-spin text-purple-600" />}
            </h3>

            <div className="space-y-4">
              <button
                onClick={handleScanReadiness}
                disabled={scanningReadiness}
                className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50/50 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Global Data Readiness</div>
                  <div className="text-xs text-gray-500">Scan all connected integrations (Code, Logs, Metrics)</div>
                </div>
              </button>

              {/* In-context Report Display */}
              {(readinessReport || scanningReadiness) && (
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-sm animate-in fade-in slide-in-from-top-2">
                  {scanningReadiness ? (
                    <div className="text-center text-gray-500 py-2">
                      <span className="block font-medium text-gray-700 mb-1">Analyzing Ecosystem...</span>
                      Scanning services, owners, and runbooks
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                        <div>
                          <span className="block font-medium text-gray-900">Overall Score</span>
                          <span className="text-xs text-gray-500">Aggregated Readiness</span>
                        </div>
                        <span className={`text-xl font-bold ${readinessReport?.overall_score > 0.8 ? 'text-green-600' : 'text-orange-600'}`}>
                          {(readinessReport?.overall_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="space-y-3">
                        {readinessReport?.details && Object.entries(readinessReport.details).map(([key, detail]) => (
                          <div key={key} className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="font-medium text-gray-700 capitalize">{key.split('-')[0]}</span>
                              <span className="text-gray-500">{(detail.readiness_score * 100).toFixed(0)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${detail.readiness_score > 0.8 ? 'bg-green-500' : 'bg-orange-400'}`}
                                style={{ width: `${detail.readiness_score * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <h3 className="text-base font-semibold text-gray-900 mb-4">Select Repository</h3>

          <div className="space-y-4">
            {/* Connected Repos List */}
            {(githubConnected || scannedRepos.length > 0) ? (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Connected Repositories
                  </h4>
                  <span className="text-xs text-blue-600 font-medium">
                    {githubRepos.length} Available
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto custom-scrollbar p-2 bg-white">
                  {/* GitHub Repos */}
                  {githubConnected && githubRepos.map((repo, idx) => {
                    const isScanned = scannedRepos.some(s => s.repo_key === repo.full_name || s.repo_path?.includes(repo.full_name));
                    return (
                      <button
                        key={`gh-${idx}`}
                        onClick={() => scanGithubRepo(repo.full_name)}
                        disabled={loading}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 rounded-lg text-sm group transition-all mb-1 last:mb-0"
                      >
                        <div className="flex items-center gap-3 truncate">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isScanned ? 'bg-green-100' : 'bg-gray-100 group-hover:bg-white group-hover:shadow-sm group-hover:border group-hover:border-gray-200'} transition-all`}>
                            {isScanned ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Shield className="w-4 h-4 text-gray-400" />}
                          </div>
                          <div className="text-left truncate">
                            <div className="font-medium text-gray-900 truncate max-w-[180px]">{repo.full_name}</div>
                            <div className="text-xs text-gray-500">{isScanned ? 'Ready to view' : 'Not scanned'}</div>
                          </div>
                        </div>
                        {!isScanned && (
                          <div className="px-3 py-1 bg-white border border-gray-200 rounded text-xs font-medium text-gray-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                            Scan
                          </div>
                        )}
                      </button>
                    );
                  })}

                  {/* Scanned Repos (deduplicate) */}
                  {scannedRepos
                    .filter(r => !githubRepos.some(gh => gh.full_name === r.repo_key))
                    .map((repo, idx) => (
                      <button
                        key={`scanned-${idx}`}
                        onClick={() => selectRepo(repo)}
                        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 rounded-lg text-sm group transition-all mb-1 last:mb-0"
                      >
                        <div className="flex items-center gap-3 truncate">
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Shield className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="text-left truncate">
                            <div className="font-medium text-gray-900 truncate max-w-[180px]">
                              {repo.repo_key || repo.repo_path?.split('/').pop() || 'Unknown'}
                            </div>
                            {repo.latest_scan && (
                              <div className={`text-xs ${repo.latest_scan.readiness_score > 0.8 ? 'text-green-600' : 'text-orange-600'}`}>
                                {repo.latest_scan.readiness_score}% Readiness
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No repositories connected</p>
                <p className="text-xs text-gray-400 mt-1">Connect GitHub to see your infra</p>
              </div>
            )}
          </div>
        </div>
      </div>




      {
        error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Scan Error</span>
            </div>
            <p className="mt-2 text-sm text-red-600">{error}</p>
          </div>
        )
      }

      {
        !scanResult && !loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <Folder className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">Ready to Scan</p>
              <p className="text-sm text-gray-500 text-center max-w-md">
                Enter a repository path above and click "Scan" to analyze the operational readiness of your infrastructure.
              </p>
            </div>
          </div>
        )
      }

      {
        loading && !scanResult && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              <p className="mt-4 text-gray-600">Scanning repository for infrastructure artifacts...</p>
              <p className="mt-2 text-sm text-gray-500">{repoPath}</p>
            </div>
          </div>
        )
      }

      {
        scanResult && (
          <>
            {/* Score Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-start gap-8">
                <div className="flex-shrink-0">
                  <ScoreGauge score={scanResult.readiness_score} />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Readiness Score Breakdown</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Weighted score based on alert coverage, ownership, dashboards, and runbook quality.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <BreakdownBar
                      label="Alert-Runbook Linkage"
                      value={scanResult.readiness_breakdown.alert_runbook_linkage}
                      weight={40}
                      icon={AlertTriangle}
                    />
                    <BreakdownBar
                      label="Owner Coverage"
                      value={scanResult.readiness_breakdown.owner_coverage}
                      weight={15}
                      icon={Server}
                    />
                    <BreakdownBar
                      label="Dashboard Coverage"
                      value={scanResult.readiness_breakdown.dashboard_coverage}
                      weight={15}
                      icon={BarChart3}
                    />
                    <BreakdownBar
                      label="Runbook Quality"
                      value={scanResult.readiness_breakdown.runbook_quality}
                      weight={30}
                      icon={Book}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="flex overflow-x-auto">
                  {[
                    { id: 'overview', label: 'Overview', icon: Shield },
                    { id: 'artifacts', label: 'Artifacts', icon: Folder },
                    { id: 'graph', label: 'Knowledge Graph', icon: Share2 },
                    { id: 'issues', label: `Issues (${scanResult.issues.length})`, icon: AlertTriangle },
                    { id: 'recommendations', label: `Recommendations (${scanResult.recommendations.length})`, icon: CheckCircle },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-gray-900">{scanResult.total_artifacts}</div>
                        <div className="text-sm text-gray-500">Total Artifacts</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-gray-900">{scanResult.services_discovered.length}</div>
                        <div className="text-sm text-gray-500">Services</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-gray-900">{scanResult.owners_discovered.length}</div>
                        <div className="text-sm text-gray-500">Owners</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-red-600">{scanResult.issues.length}</div>
                        <div className="text-sm text-gray-500">Issues Found</div>
                      </div>
                    </div>

                    {/* Services List */}
                    {scanResult.services_discovered.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Discovered Services</h4>
                        <div className="flex flex-wrap gap-2">
                          {scanResult.services_discovered.map((service, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Owners List */}
                    {scanResult.owners_discovered.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Discovered Owners</h4>
                        <div className="flex flex-wrap gap-2">
                          {scanResult.owners_discovered.map((owner, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              {owner}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'artifacts' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <ArtifactCard
                      title="Alerts"
                      count={scanResult.alerts.length}
                      icon={AlertTriangle}
                      items={scanResult.alerts}
                      color="red"
                    />
                    <ArtifactCard
                      title="Runbooks"
                      count={scanResult.runbooks.length}
                      icon={Book}
                      items={scanResult.runbooks}
                      color="teal"
                    />
                    <ArtifactCard
                      title="Dashboards"
                      count={scanResult.dashboards.length}
                      icon={BarChart3}
                      items={scanResult.dashboards}
                      color="blue"
                    />
                    <ArtifactCard
                      title="K8s Resources"
                      count={scanResult.k8s_resources.length}
                      icon={Server}
                      items={scanResult.k8s_resources}
                      color="purple"
                    />
                    <ArtifactCard
                      title="Helm Charts"
                      count={scanResult.helm_charts.length}
                      icon={GitBranch}
                      items={scanResult.helm_charts}
                      color="green"
                    />
                    <ArtifactCard
                      title="CI/CD Pipelines"
                      count={scanResult.ci_pipelines.length}
                      icon={GitBranch}
                      items={scanResult.ci_pipelines}
                      color="orange"
                    />
                    <ArtifactCard
                      title="Terraform Modules"
                      count={scanResult.terraform_modules.length}
                      icon={FileCode}
                      items={scanResult.terraform_modules}
                      color="purple"
                    />
                    <ArtifactCard
                      title="Ansible Configs"
                      count={scanResult.ansible_configs.length}
                      icon={FileCode}
                      items={scanResult.ansible_configs}
                      color="blue"
                    />
                    <ArtifactCard
                      title="Docker Configs"
                      count={scanResult.docker_configs.length}
                      icon={Container}
                      items={scanResult.docker_configs}
                      color="blue"
                    />
                  </div>
                )}

                {activeTab === 'issues' && (
                  <div className="space-y-3">
                    {scanResult.issues.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                        <p className="font-medium">No critical issues found!</p>
                        <p className="text-sm mt-1">Your repository meets basic readiness requirements.</p>
                      </div>
                    ) : (
                      scanResult.issues.map((issue, idx) => (
                        <IssueItem key={idx} issue={issue} />
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'recommendations' && (
                  <div className="space-y-3">
                    {scanResult.recommendations.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                        <p className="font-medium">No recommendations at this time.</p>
                        <p className="text-sm mt-1">Your infrastructure is well-documented.</p>
                      </div>
                    ) : (
                      scanResult.recommendations.map((rec, idx) => (
                        <RecommendationItem key={idx} recommendation={rec} />
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'graph' && (
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Infrastructure Knowledge Graph</h3>
                      <p className="text-sm text-gray-500">
                        Visualize relationships between your infrastructure components, monitoring configs,
                        CI/CD pipelines, and documentation. Click "Build Graph" to pre-create the knowledge graph.
                      </p>
                    </div>
                    <KnowledgeGraphVisualization
                      repoPath={scanResult.repo_path}
                      onNodeClick={setSelectedGraphNode}
                    />
                    {selectedGraphNode && (
                      <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            {selectedGraphNode.fullLabel || selectedGraphNode.label}
                          </h4>
                          <button
                            onClick={() => setSelectedGraphNode(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Type:</span>
                            <span
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: selectedGraphNode.backgroundColor,
                                color: selectedGraphNode.color,
                              }}
                            >
                              {selectedGraphNode.nodeType}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Category:</span>
                            <span className="text-gray-700 capitalize">{selectedGraphNode.category}</span>
                          </div>
                          {selectedGraphNode.properties?.file_path && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">File:</span>
                              <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                                {selectedGraphNode.properties.file_path}
                              </code>
                            </div>
                          )}
                          {selectedGraphNode.properties?.service && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Service:</span>
                              <span className="text-gray-700">{selectedGraphNode.properties.service}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Artifact Graph Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Artifact Relationships</h3>
                <a
                  href="/api/oncall/graph"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  View Full Graph <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <div className="inline-flex items-center gap-8">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-2">
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Alerts</span>
                    <span className="text-xs text-gray-500">{scanResult.alerts.length}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-16 h-0.5 bg-gray-300"></div>
                    <span className="text-xs text-gray-400 px-2">links to</span>
                    <div className="w-16 h-0.5 bg-gray-300"></div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-2">
                      <Book className="w-8 h-8 text-teal-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Runbooks</span>
                    <span className="text-xs text-gray-500">{scanResult.runbooks.length}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-16 h-0.5 bg-gray-300"></div>
                    <span className="text-xs text-gray-400 px-2">documents</span>
                    <div className="w-16 h-0.5 bg-gray-300"></div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                      <Server className="w-8 h-8 text-blue-500" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Services</span>
                    <span className="text-xs text-gray-500">{scanResult.services_discovered.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }
    </div >
  );
}
